// app/src/modules/ui/Dialog.tsx — app-wide styled confirm / prompt / toast (Bootstrap).
// Usage (from anywhere): await confirmDialog({...}), await promptDialog({...}), toast("Saved", "success").
import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

export type ConfirmOpts = { title?: string; message: string; confirmLabel?: string; cancelLabel?: string; danger?: boolean };
export type PromptOpts = { title?: string; message?: string; placeholder?: string; defaultValue?: string; confirmLabel?: string; cancelLabel?: string; multiline?: boolean; required?: boolean };
export type ToastKind = "info" | "success" | "error";

type DialogState =
  | { kind: "confirm"; opts: ConfirmOpts; resolve: (v: boolean) => void }
  | { kind: "prompt"; opts: PromptOpts; resolve: (v: string | null) => void }
  | null;
type ToastItem = { id: number; message: string; kind: ToastKind };

let _openConfirm: ((o: ConfirmOpts) => Promise<boolean>) | null = null;
let _openPrompt: ((o: PromptOpts) => Promise<string | null>) | null = null;
let _toast: ((m: string, k: ToastKind) => void) | null = null;

/** Styled confirm. Falls back to native confirm if the host isn't mounted. */
export function confirmDialog(opts: ConfirmOpts): Promise<boolean> {
  if (_openConfirm) return _openConfirm(opts);
  return Promise.resolve(window.confirm(opts.message));
}
/** Styled text prompt. Resolves to the entered string, or null if cancelled. */
export function promptDialog(opts: PromptOpts): Promise<string | null> {
  if (_openPrompt) return _openPrompt(opts);
  return Promise.resolve(window.prompt(opts.message ?? opts.title ?? "", opts.defaultValue ?? ""));
}
/** Transient toast notification. */
export function toast(message: string, kind: ToastKind = "info") {
  if (_toast) _toast(message, kind);
}

/** Run a mutating action with standard UI feedback: a success toast on completion and
 *  an error toast (the server's message, or a fallback) if it throws. Returns the action's
 *  result, or undefined on failure — so callers can guard state updates on success. */
export async function runWithToast<T>(
  action: () => Promise<T>,
  success?: string,
  errorFallback = "Something went wrong",
): Promise<T | undefined> {
  try {
    const result = await action();
    if (success) toast(success, "success");
    return result;
  } catch (e: any) {
    toast(e?.message || errorFallback, "error");
    return undefined;
  }
}

const inputStyle: CSSProperties = { height: 46, width: "100%", borderRadius: 4, border: "1.5px solid var(--line)", background: "var(--card)", padding: "0 12px", fontSize: 15, color: "var(--ink)" };
const descStyle: CSSProperties = { fontSize: 15, lineHeight: 1.5, color: "var(--ink-2)" };

export function UiHost() {
  const [dialog, setDialog] = useState<DialogState>(null);
  const [value, setValue] = useState("");
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const tid = useRef(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    _openConfirm = (opts) => new Promise<boolean>((resolve) => setDialog({ kind: "confirm", opts, resolve }));
    _openPrompt = (opts) => new Promise<string | null>((resolve) => { setValue(opts.defaultValue ?? ""); setDialog({ kind: "prompt", opts, resolve }); });
    _toast = (message, kind) => {
      const id = ++tid.current;
      setToasts((t) => [...t, { id, message, kind }]);
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3600);
    };
    return () => { _openConfirm = null; _openPrompt = null; _toast = null; };
  }, []);

  // On open: remember the trigger, lock body scroll, and set initial focus (prompt → input;
  // confirm → Cancel, so a destructive default is never the pre-focused control). On close:
  // restore body scroll and return focus to the invoking control.
  useEffect(() => {
    if (!dialog) return;
    restoreRef.current = (document.activeElement as HTMLElement) ?? null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const h = setTimeout(() => {
      if (dialog.kind === "prompt") inputRef.current?.focus();
      else cancelRef.current?.focus();
    }, 40);
    return () => {
      clearTimeout(h);
      document.body.style.overflow = prevOverflow;
      restoreRef.current?.focus?.();
    };
  }, [dialog]);

  const close = useCallback((result: boolean | string | null) => {
    setDialog((d) => {
      if (d) { if (d.kind === "confirm") d.resolve(result as boolean); else d.resolve(result as string | null); }
      return null;
    });
  }, []);

  useEffect(() => {
    if (!dialog) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); close(dialog.kind === "confirm" ? false : null); }
      else if (e.key === "Tab" && dialogRef.current) {
        const f = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'));
        if (f.length) {
          const first = f[0], last = f[f.length - 1], active = document.activeElement as HTMLElement | null;
          if (!dialogRef.current.contains(active)) { e.preventDefault(); first.focus(); }
          else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
        }
      }
      else if (e.key === "Enter" && (dialog.kind === "confirm" || !(dialog.opts as PromptOpts).multiline)) {
        if (dialog.kind === "prompt" && (dialog.opts as PromptOpts).required && !value.trim()) return;
        e.preventDefault();
        close(dialog.kind === "confirm" ? true : value);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dialog, value, close]);

  return (
    <>
      {dialog && (
        <div className="position-fixed d-grid" style={{ inset: 0, zIndex: 1000, placeItems: "center", background: "rgba(0,0,0,.5)", padding: "max(20px,env(safe-area-inset-top)) 16px" }} onClick={() => close(dialog.kind === "confirm" ? false : null)} role="presentation">
          <div ref={dialogRef} className="d-grid" style={{ maxHeight: "90vh", width: "min(440px,100%)", gap: 12, overflowY: "auto", borderRadius: "var(--r-lg)", border: "1px solid var(--line)", background: "var(--card)", padding: 24, boxShadow: "var(--shadow-pop)" }} onClick={(e) => e.stopPropagation()} role={dialog.kind === "confirm" ? "alertdialog" : "dialog"} aria-modal="true" aria-labelledby={dialog.opts.title ? "ui-dialog-title" : undefined} aria-label={dialog.opts.title ? undefined : "Dialog"} aria-describedby={(dialog.kind === "confirm" ? dialog.opts.message : (dialog.opts as PromptOpts).message) ? "ui-dialog-desc" : undefined}>
            {dialog.opts.title && <div id="ui-dialog-title" className="display" style={{ fontSize: "1.25rem", color: "var(--ink)" }}>{dialog.opts.title}</div>}
            {dialog.kind === "confirm" && <p id="ui-dialog-desc" style={descStyle}>{dialog.opts.message}</p>}
            {dialog.kind === "prompt" && (<>
              {dialog.opts.message && <p id="ui-dialog-desc" style={descStyle}>{dialog.opts.message}</p>}
              {(dialog.opts as PromptOpts).multiline
                ? <textarea ref={inputRef} aria-label={dialog.opts.title || "Input"} aria-describedby={(dialog.opts as PromptOpts).message ? "ui-dialog-desc" : undefined} aria-required={(dialog.opts as PromptOpts).required ? true : undefined} style={{ height: 92, width: "100%", borderRadius: 4, border: "1.5px solid var(--line)", background: "var(--card)", padding: 10, fontSize: 15, color: "var(--ink)" }} placeholder={dialog.opts.placeholder} value={value} onChange={(e) => setValue(e.target.value)} />
                : <input ref={inputRef} aria-label={dialog.opts.title || "Input"} aria-describedby={(dialog.opts as PromptOpts).message ? "ui-dialog-desc" : undefined} aria-required={(dialog.opts as PromptOpts).required ? true : undefined} style={inputStyle} placeholder={dialog.opts.placeholder} value={value} onChange={(e) => setValue(e.target.value)} />}
            </>)}
            <div className="d-flex flex-wrap justify-content-end" style={{ marginTop: 6, gap: 10 }}>
              <button ref={cancelRef} className="fw-bold" style={{ height: 44, borderRadius: 4, border: "1px solid var(--line)", background: "var(--paper-2)", padding: "0 18px", fontSize: 15, color: "var(--ink)" }} onClick={() => close(dialog.kind === "confirm" ? false : null)}>{dialog.opts.cancelLabel || "Cancel"}</button>
              <button
                className="fw-bold"
                style={{ height: 44, borderRadius: 4, padding: "0 20px", fontSize: 15, ...(dialog.kind === "confirm" && dialog.opts.danger ? { background: "var(--danger)", color: "#fff" } : { background: "var(--ink)", color: "var(--paper)" }) }}
                disabled={dialog.kind === "prompt" && !!(dialog.opts as PromptOpts).required && !value.trim()}
                onClick={() => close(dialog.kind === "confirm" ? true : value)}
              >{dialog.opts.confirmLabel || (dialog.kind === "confirm" ? "Confirm" : "OK")}</button>
            </div>
          </div>
        </div>
      )}

      {toasts.length > 0 && (
        <div className="position-fixed d-flex flex-column align-items-center" style={{ pointerEvents: "none", left: 0, right: 0, bottom: "max(20px,env(safe-area-inset-bottom))", zIndex: 1100, gap: 8, paddingLeft: 16, paddingRight: 16 }} aria-live="polite">
          {toasts.map((t) => (
            <div key={t.id} className="text-center fw-semibold" style={{ maxWidth: 460, borderRadius: 4, padding: "12px 20px", fontSize: 14, boxShadow: "var(--shadow-md)", ...(t.kind === "success" ? { background: "var(--green)", color: "var(--accent-ink)" } : t.kind === "error" ? { background: "var(--accent)", color: "var(--accent-ink)" } : { background: "var(--ink)", color: "var(--paper)" }) }}>{t.message}</div>
          ))}
        </div>
      )}
    </>
  );
}
